import './ServerMessage.scss'

import { Box, Flex } from '@chakra-ui/react'
import { mdiHeadset } from '@mdi/js'
import Icon from '@mdi/react'
import { PrimitiveAtom, useAtom } from 'jotai'
import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'

import { Container } from '@/components/ui/container'
import { Text } from '@/components/ui/typography'
import { chatStatusAtom, getStatus, getTopLabel, mapAgentColors } from '@/store/gigiChat/Visuals'
import { IShortMessageObject, ISystemAgentVisuals } from '@/types/gigiChat/Chat'
import { IChatStatus } from '@/types/gigiChat/LiveAgent'

import LastResponseTime from '../liveAgent/lastResponseTime'
import AnsweMarkDown from './components/AnswerMarkDown'
import SummarizeQuestionMarkdown from './components/summarizeQuestion/SummarizeQuestionMarkDown'
import UploadQuestionMarkup from './components/uploadQuestion'
import ErrorMessage from './ErrorMessage'

const isFirstWordSummarizeOrUploaded = (input: string): boolean => {
    if (input) {
        const [firstWord] = input.split(' ', 1)
        return firstWord === 'Summarize' || firstWord === 'Uploaded'
    }
    return false
}

const ServerMessage = React.memo(
    ({
        item,
        question,
        answer,
        displayAgentLabel,
        isAgent,
        agentInfo,
    }: {
        item: IShortMessageObject
        question: string
        answer: string | null
        displayAgentLabel: boolean
        isAgent: boolean
        agentInfo: ISystemAgentVisuals | null
    }) => {
        const [chatVisuals] = useAtom<PrimitiveAtom<IChatStatus | null>>(chatStatusAtom)
        const agentLabel = getTopLabel(chatVisuals)
        const status = getStatus(item.userPromptId)
        const agentColor = mapAgentColors(agentInfo?.statusColors?.find((sc) => sc.status === status)?.color)

        if (isAgent && !answer) {
            return (
                <Container my="1" maxWidth="unset" variant="user">
                    <Text className="conversation-message question" lineHeight="20px" fontSize="14px" as="div" overflowX="auto">
                        <ReactMarkdown remarkPlugins={[remarkBreaks]}>{question || ''}</ReactMarkdown>
                    </Text>
                </Container>
            )
        }

        return (
            <>
                <Container my="1" maxWidth="unset" variant="user">
                    <Text className="conversation-message question" lineHeight="20px" fontSize="14px" as="div" overflowX="auto">
                        {isFirstWordSummarizeOrUploaded(question) ? (
                            <>
                                <SummarizeQuestionMarkdown value={question} />
                                <UploadQuestionMarkup value={question} />
                            </>
                        ) : (
                            <ReactMarkdown remarkPlugins={[remarkBreaks]}>{question || ''}</ReactMarkdown>
                        )}
                    </Text>
                </Container>
                {displayAgentLabel && agentLabel && (
                    <Flex py="12px" fontSize="12px">
                        <Box pr="12px">
                            <Icon path={mdiHeadset} color={agentColor} size="16px" />
                        </Box>
                        <Box fontSize={'xs'}>{agentLabel}</Box>
                    </Flex>
                )}
                {answer && !item.instructionalErrorMessageId && (
                    <Container
                        variant="server"
                        mt="1"
                        maxWidth="unset"
                        borderRadius={item.citations && item.citations.length > 0 ? '' : '4px'}
                        borderTopRadius={item.citations && item.citations.length > 0 ? '4px' : ''}
                        id={item.userPromptId?.toString()}
                    >
                        <Text lineHeight="20px" fontSize="14px" as="div" overflowX="auto" className="conversation-message answer">
                            <AnsweMarkDown answer={answer} />
                            {isAgent && <LastResponseTime promptId={item.userPromptId} />}
                        </Text>
                    </Container>
                )}
                {answer && !item.isStreaming && item.instructionalErrorMessageId && <ErrorMessage message={answer} />}
                {!answer && !item.isStreaming && <ErrorMessage />}
            </>
        )
    },
)

ServerMessage.displayName = 'ServerMessage'

export default ServerMessage
